/**
 * src/game/framework/GamesRegistry.ts
 *
 * In-process registry of GameManifest entries. Each game module calls
 * `registerGame(...)` from its top-level so the UI can enumerate all
 * available games without hard-coding them in the GamesRail / GameManager.
 *
 * Goes hand-in-hand with `GAMES_SYSTEM_AUDIT.md` §7.1. This file is the
 * minimum needed to ship FreeDoom today — the full GameActivityManager
 * lands separately when multiplayer is re-introduced.
 */

import type { GameId, GameManifest } from './types';

const registry = new Map<GameId, GameManifest>();

/**
 * Idempotent. If the same id is re-registered, the second call replaces the
 * first. Useful for HMR without leaking duplicates.
 */
export function registerGame(manifest: GameManifest): void {
  registry.set(manifest.id, manifest);
}

/**
 * Removes a previously registered game. Returns true if a removal occurred.
 */
export function unregisterGame(id: GameId): boolean {
  return registry.delete(id);
}

/**
 * All currently registered games. The result is a fresh array — mutating it
 * does not affect the registry.
 */
export function listGames(): GameManifest[] {
  return [...registry.values()];
}

/**
 * Resolves a single manifest by id, or returns undefined.
 */
export function getGame(id: GameId): GameManifest | undefined {
  return registry.get(id);
}

/**
 * Splits the registry by `multiplayer` and `available`. Used by GamesRail UI.
 */
export function listGamesByCategory(): {
  multiplayer: GameManifest[];
  singlePlayer: GameManifest[];
} {
  const all = listGames().filter((g) => g.available);
  return {
    multiplayer: all.filter((g) => g.multiplayer),
    singlePlayer: all.filter((g) => !g.multiplayer),
  };
}

/**
 * Smoke-test for unit tests / migrations: export the registry's bookkeeping
 * without exposing the underlying Map.
 */
export function registrySize(): number {
  return registry.size;
}
